import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import { Button } from "@/components/ui/Button";
import { PermissionButton } from "@/components/ui/PermissionButton";
import ButtonGroup from "@/components/ui/ButtonGroup";
import DateBox from "@/components/ui/DateBox";
import DateSearchBtn from "@/components/ui/DateSearchBtn";
import { InputBox } from "@/components/ui/InputBox";
import SelectBox from "@/components/ui/SelectBox";
import CountBox from "@/components/ui/CountBox";
import { useState, useMemo, useEffect } from "react";
import Table, { TableColumn } from "@/components/ui/Table";
import {
  useOrderSlipReport,
  useDeleteOrderSlips,
  useHardDeleteOrderSlips,
  useRestoreOrderSlips,
} from "@/hooks/dispatch/dp_ordeer_slip/useOrderSlip";
import { useCodeListByCompanyCode, useInfiniteScrollPage } from "@/hooks";
import { toast } from "sonner";
import { useUserInfo } from "@/hooks/member/useMember";

export default function OrderDel() {
  // React Query 훅으로 회사 데이터 가져오기
  const { data: companyCodeList = [] } = useCodeListByCompanyCode();
  // 사용자 정보 가져오기
  const { data: userInfo } = useUserInfo();

  // 삭제 mutation (soft delete)
  const deleteOrderSlipsMutation = useDeleteOrderSlips();
  // 하드 삭제 mutation (영구 삭제)
  const hardDeleteOrderSlipsMutation = useHardDeleteOrderSlips();
  // 복구 mutation
  const restoreOrderSlipsMutation = useRestoreOrderSlips();

  // 입력용 state (UI에서 사용)
  const [searchValue, setSearchValue] = useState("");

  // 검색 필드 선택 및 검색어
  const [searchField, setSearchField] = useState("custName"); // 기본값: 거래처
  const [searchKeyword, setSearchKeyword] = useState(""); // 검색어 입력값
  const [searchField2, setSearchField2] = useState("carNumber"); // 두 번째 검색 필드 기본값: 차량번호
  const [searchKeyword2, setSearchKeyword2] = useState(""); // 두 번째 검색어 입력값

  // 실제 적용된 검색 조건 (조회 버튼 클릭 시 적용)
  const [appliedSearchDto, setAppliedSearchDto] = useState<{
    companyCode?: string;
    custName?: string;
    carNumber?: string;
    driverName?: string;
    driverTel?: string;
    inLocation?: string;
    outLocation?: string;
    outArea1?: string;
    outArea2?: string;
    dispatchStaffName?: string;
    itemName?: string;
    delYn?: string;
  }>({ delYn: "Y" }); // 삭제된 항목만 조회

  // 이번 달 1일 ~ 오늘 날짜를 기본값으로 설정 (주문일자는 즉시 반영)
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  // 로컬 날짜 문자열로 변환 (UTC 변환 방지)
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const firstDayStr = `${firstDayOfMonth.getFullYear()}-${String(firstDayOfMonth.getMonth() + 1).padStart(2, "0")}-01`;

  const [startDate, setStartDate] = useState(firstDayStr);
  const [endDate, setEndDate] = useState(todayStr);

  const [selectedValue, setSelectedValue] = useState("all");
  const [selectedValue2, setSelectedValue2] = useState("all2");
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [dateBtnValue, setDateBtnValue] = useState("today");
  const [currentPage, setCurrentPage] = useState(0);
  const [allData, setAllData] = useState<any[]>([]); // 누적된 데이터
  const [tableVer, setTableVer] = useState(0); // 테이블 버전 (리렌더링용)

  // 주문 전표 리포트 조회 (delYn: "Y"로 조회)
  const { data: orderSlipData, isLoading } = useOrderSlipReport({
    page: currentPage,
    size: 20,
    dateFrom: startDate,
    dateTo: endDate,
    searchDto: {
      ...appliedSearchDto,
      companyCode: searchValue || undefined, // 회사는 즉시 반영
      delYn: "Y", // 삭제된 항목만 조회
    },
  });

  const totalElements = orderSlipData?.totalElements || 0;
  const totalPages = orderSlipData?.totalPages || 0;

  // 조회 조건이 변경되면 데이터 초기화 및 테이블 버전 업데이트
  useEffect(() => {
    setAllData([]);
    setCurrentPage(0);
    setTableVer(prev => prev + 1); // 검색 조건 변경 시 테이블 리렌더링
  }, [startDate, endDate, searchValue, appliedSearchDto]);

  // 데이터 누적 로직
  useEffect(() => {
    if (orderSlipData && orderSlipData.content && orderSlipData.content.length > 0) {
      if (currentPage === 0) {
        // 첫 페이지면 데이터 초기화
        setAllData(orderSlipData.content);
      } else {
        // 다음 페이지면 기존 데이터에 추가
        setAllData(prev => [...prev, ...orderSlipData.content]);
      }
    } else if (orderSlipData && (!orderSlipData.content || orderSlipData.content.length === 0)) {
      // 데이터가 없는 경우 빈 배열로 설정
      setAllData([]);
    }
  }, [orderSlipData, currentPage]);

  // 주문 유형에 따라 데이터 필터링 (실시간)
  const filteredData = useMemo(() => {
    if (selectedValue === "all") {
      return allData; // 전체
    } else if (selectedValue === "option1") {
      return allData.filter(item => item.saleProcessed === true); // 출고 (매출처리됨)
    } else if (selectedValue === "option2") {
      return allData.filter(item => !item.saleProcessed); // 미출고 (매출처리 안됨)
    }
    return allData;
  }, [allData, selectedValue]);

  // 주문 유형 필터가 변경될 때만 테이블 버전 업데이트 (테이블 리렌더링)
  useEffect(() => {
    setTableVer(prev => prev + 1);
  }, [selectedValue]);

  const hasMore = useMemo(() => {
    if (!orderSlipData) return false;
    return !orderSlipData.last && allData.length < totalElements;
  }, [orderSlipData, allData.length, totalElements]);

  // 무한 스크롤 설정
  const infiniteScrollPage = useInfiniteScrollPage({
    data: filteredData, // 필터링된 데이터 사용
    initialLoadCount: 20,
    loadMoreCount: 20,
    hasMore: hasMore,
    onDataLoad: async () => {
      if (hasMore && !isLoading) {
        setCurrentPage(prev => prev + 1);
      }
    },
  });

  const orderList = filteredData; // 필터링된 데이터를 orderList로 사용

  // 테이블 컬럼 정의 (요구사항에 맞게 구성)
  const columns: TableColumn[] = [
    { key: "orderSlipWriteId2", header: "삭제ID", sticky: "left" },
    { key: "orderDate", header: "주문일자", sticky: "left" },
    {
      key: "arrivalTime",
      header: "도착시간",
      sticky: "left",
      render: (value: string) => {
        if (!value) return "";
        return value.replace("T", " ").replace(/\.\d{3}Z?$/, "");
      },
    },
    { key: "carNumber", header: "차량번호", bodyAlign: "left", group: "차량정보" },
    { key: "driverName", header: "기사명", group: "차량정보" },
    { key: "driverTel", header: "전화번호", group: "차량정보" },
    { key: "itemName", header: "품목명", bodyAlign: "left" },
    { key: "custName", header: "거래처", bodyAlign: "left" },
    { key: "orderQty", header: "주문수량", bodyAlign: "right" },
    { key: "inBranch", header: "대리점", bodyAlign: "left", group: "상차정보" },
    { key: "inLocation", header: "상차지", bodyAlign: "left", group: "상차정보" },
    { key: "inQty", header: "수량", bodyAlign: "right", group: "상차정보" },
    { key: "outLocation1", header: "하차지1", bodyAlign: "left", group: "하차정보" },
    { key: "outArea1", header: "지역1", bodyAlign: "left", group: "하차정보" },
    { key: "outQty1", header: "수량1", bodyAlign: "right", group: "하차정보" },
    { key: "outLocation2", header: "하차지2", bodyAlign: "left", group: "하차정보" },
    { key: "outArea2", header: "지역2", bodyAlign: "left", group: "하차정보" },
    { key: "outQty2", header: "수량2", bodyAlign: "right", group: "하차정보" },
    { key: "outLocation3", header: "하차지3", bodyAlign: "left", group: "하차정보" },
    { key: "outArea3", header: "지역3", bodyAlign: "left", group: "하차정보" },
    { key: "outQty3", header: "수량3", bodyAlign: "right", group: "하차정보" },
    { key: "dispatchStaffCode", header: "배차사원" },
    { key: "remark", header: "비고", bodyAlign: "left" },
  ];

  // API 데이터를 SelectBox 옵션으로 변환
  const companyOptions = useMemo(() => {
    return companyCodeList.map((item: any) => ({
      value: item.code || item.codeId?.toString() || "",
      label: item.name || "",
    }));
  }, [companyCodeList]);

  // 검색 필드 옵션
  const searchFieldOptions = [
    { label: "거래처", value: "custName" },
    { label: "차량번호", value: "carNumber" },
    { label: "기사명", value: "driverName" },
    { label: "전화번호", value: "driverTel" },
    { label: "상차지", value: "inLocation" },
    { label: "하차지", value: "outLocation" },
    { label: "하차지역1", value: "outArea1" },
    { label: "하차지역2", value: "outArea2" },
    { label: "배차사원", value: "dispatchStaffName" },
    { label: "품목명", value: "itemName" },
  ];

  const handleReset = () => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    // 로컬 날짜 문자열로 변환 (UTC 변환 방지)
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const firstDayStr = `${firstDayOfMonth.getFullYear()}-${String(firstDayOfMonth.getMonth() + 1).padStart(2, "0")}-01`;

    setSearchValue("");
    setSearchField("custName");
    setSearchKeyword("");
    setSearchField2("carNumber");
    setSearchKeyword2("");
    setAppliedSearchDto({ delYn: "Y" });
    setStartDate(firstDayStr);
    setEndDate(todayStr);
    setSelectedValue("all");
    setSelectedValue2("all2");
    setDateBtnValue("today");
    // 페이지 초기화
    setAllData([]);
    setCurrentPage(0);
    setTableVer(v => v + 1);
  };

  const handleSearch = () => {
    // 선택된 필드에 맞게 검색 조건 구성
    const newSearchDto: typeof appliedSearchDto = {
      delYn: "Y",
    };

    // 검색어가 있으면 선택된 필드에 매핑
    if (searchKeyword.trim()) {
      newSearchDto[searchField as keyof typeof newSearchDto] = searchKeyword.trim();
    }
    // 두 번째 검색어가 있으면 선택된 필드에 매핑
    if (searchKeyword2.trim()) {
      newSearchDto[searchField2 as keyof typeof newSearchDto] = searchKeyword2.trim();
    }

    // 검색 조건 적용
    setAppliedSearchDto(newSearchDto);
    // 페이지 초기화
    setAllData([]);
    setCurrentPage(0);
    setTableVer(v => v + 1);
  };

  // 자료복구 핸들러
  const handleRestore = () => {
    if (selectedRows.length === 0) {
      toast.warning("복구할 항목을 선택해주세요.");
      return;
    }

    // 선택된 행들의 데이터 가져오기
    const selectedItems = selectedRows.map(index => orderList[index]).filter(item => item && item.orderSlipCode);

    // 매출이 처리된 항목이 있는지 확인
    const itemsWithSale = selectedItems.filter(item => item.saleProcessed === true);
    if (itemsWithSale.length > 0) {
      toast.error(`매출이 처리된 항목(${itemsWithSale.length}개)은 매출자료복구에서 복구해야 합니다.`);
      return;
    }

    // 선택된 행들의 orderSlipCode 수집
    const orderSlipCodesToRestore = selectedItems.map(item => item.orderSlipCode) as string[];

    if (orderSlipCodesToRestore.length === 0) {
      toast.error("복구할 수 있는 항목이 없습니다.");
      return;
    }

    // 확인 대화상자
    if (window.confirm(`선택한 ${orderSlipCodesToRestore.length}개의 주문 전표를 복구하시겠습니까?`)) {
      const delId = userInfo?.name || "";
      if (!delId) {
        toast.error("사용자 정보를 가져올 수 없습니다.");
        return;
      }

      restoreOrderSlipsMutation.mutate(
        { orderSlipCodes: orderSlipCodesToRestore, delId },
        {
          onSuccess: () => {
            toast.success(`${orderSlipCodesToRestore.length}개의 주문 전표가 복구되었습니다.`);
            handleReset();
          },
          onError: (error: any) => {
            console.error("복구 실패:", error);
            toast.error(error?.response?.data?.message || "주문 전표 복구에 실패했습니다.");
          },
        }
      );
    }
  };

  // 자료삭제 핸들러 (완전 삭제 - 이미 delYn이 'Y'인 항목을 영구 삭제)
  const handlePermanentDelete = () => {
    if (selectedRows.length === 0) {
      toast.warning("삭제할 항목을 선택해주세요.");
      return;
    }

    // 선택된 행들의 데이터 가져오기
    const selectedItems = selectedRows.map(index => orderList[index]).filter(item => item && item.orderSlipCode);

    // 매출이 처리된 항목이 있는지 확인
    const itemsWithSale = selectedItems.filter(item => item.saleProcessed === true);
    if (itemsWithSale.length > 0) {
      toast.error(`매출이 처리된 항목(${itemsWithSale.length}개)은 삭제할 수 없습니다.`);
      return;
    }

    // 선택된 행들의 orderSlipCode 수집
    const orderSlipCodesToDelete = selectedItems.map(item => item.orderSlipCode) as string[];

    if (orderSlipCodesToDelete.length === 0) {
      toast.error("삭제할 수 있는 항목이 없습니다.");
      return;
    }

    // 확인 대화상자
    if (
      window.confirm(
        `선택한 ${orderSlipCodesToDelete.length}개의 주문 전표를 영구 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`
      )
    ) {
      hardDeleteOrderSlipsMutation.mutate(orderSlipCodesToDelete, {
        onSuccess: () => {
          toast.success(`${orderSlipCodesToDelete.length}개의 주문 전표가 영구 삭제되었습니다.`);
          handleReset();
        },
        onError: (error: any) => {
          console.error("삭제 실패:", error);
          toast.error(error?.response?.data?.message || "주문 전표 삭제에 실패했습니다.");
        },
      });
    }
  };

  return (
    <div className="flex flex-col h-full">
      <SearchBoxLayout>
        <div className="flex items-end mb-3">
          <SelectBox
            label="회사"
            className="w-50"
            value={searchValue}
            onChange={e => setSearchValue(e)}
            options={companyOptions}
          />
          <div className="mr-3" />
          <SelectBox
            label={searchFieldOptions.find(opt => opt.value === searchField)?.label || "검색필드"}
            className="min-w-24"
            value={searchField}
            onChange={e => setSearchField(e)}
            options={searchFieldOptions}
          />
          <div className="mr-1" />
          <InputBox
            label=""
            className="w-50"
            value={searchKeyword}
            onChange={e => setSearchKeyword(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />
          <div className="mr-3" />
          <SelectBox
            label={searchFieldOptions.find(opt => opt.value === searchField2)?.label || "검색필드"}
            className="min-w-24"
            value={searchField2}
            onChange={e => setSearchField2(e)}
            options={searchFieldOptions}
          />
          <div className="mr-1" />
          <InputBox
            label=""
            className="w-50"
            value={searchKeyword2}
            onChange={e => setSearchKeyword2(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />
          <div className="mr-3" />
          <DateBox
            label="주문일자"
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
          <div className="mr-2" />
          <DateSearchBtn
            mode="range"
            value={dateBtnValue}
            onChangeValue={setDateBtnValue}
            currentRange={{ startDate, endDate }}
            onDateSelect={({ startDate, endDate }) => {
              setStartDate(startDate);
              setEndDate(endDate);
            }}
          />
        </div>
        <div className="flex items-end">
          <ButtonGroup
            label="주문유형"
            options={[
              { value: "all", label: "전체" },
              { value: "option1", label: "출고" },
              { value: "option2", label: "미출고" },
            ]}
            value={selectedValue}
            onChange={setSelectedValue}
          />
          <div className="mr-3" />
          {/* <ButtonGroup
            label=""
            options={[
              { value: "all2", label: "전체" },
              { value: "option3", label: "화주WEB 주문" },
            ]}
            value={selectedValue2}
            onChange={setSelectedValue2}
          /> */}
          <div className="flex gap-[6px] ml-auto">
            <Button variant="reset" onClick={handleReset}>
              초기화
            </Button>
            <Button variant="search" onClick={handleSearch}>
              조회
            </Button>
          </div>
        </div>
      </SearchBoxLayout>

      <main className="flex-1 px-4 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-2">
          <CountBox count={orderSlipData?.totalElements || 0} />
          <div className="flex gap-2">
            <PermissionButton permission="canUpdate" variant="primary" onClick={handleRestore}>
              자료복구
            </PermissionButton>
            <PermissionButton permission="canDelete" variant="etc" onClick={handlePermanentDelete}>
              삭제
            </PermissionButton>
          </div>
        </div>
        <div className="flex-1 min-h-0">
          <Table
            key={`order-del-table-v${tableVer}`}
            {...infiniteScrollPage.tableProps}
            resizable
            className="h-full"
            columns={columns}
            stickyCheckbox
            stickyRowNumber
            showCheckbox={true}
            showRowNumber={true}
            selectedRows={selectedRows}
            onRowSelect={setSelectedRows}
            maxHeight="100%"
          />
        </div>
      </main>

      {/* <footer className="flex-shrink-0 p-4 bg-gray-50 border-t">
        <Footer grid={footerGrid} />
      </footer> */}
    </div>
  );
}
